import React, { useState, useRef } from 'react'
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import MenuList from '@mui/material/MenuList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { isAuth, signout } from '../../helpers/cookies';
import { ADMIN_PORTAL_ACCESS, COMPANY_MANAGER, ADDNECTAR_MANAGER, COMPANY_USER, SUPER_ADMIN } from '../../helpers/constants';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setChangePasswordOpen, setMobileSearchOpen } from '../../store/user/userSlice';
import setProfileModalOpen from '../../store/profile/profileSlice';

import DownloadReportModal from '../common/DownloadReportModal';

const ToolbarAccount = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { is_open, is_mobile_search_open } = useSelector((state) => state.user);

  const auth = isAuth();

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [reportsAnchor, setReportsAnchor] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [reportType, setReportType] = useState('correction');
  const isOverMenuItem = useRef(false);
  const isOverSubmenu = useRef(false);
  const closeTimer = useRef(null);

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const userRole = auth?.role;
  let settings = ['Change Password', 'Brand Library'];

  if (!auth && !isLoggingOut) return null; // Component should not render without auth unless logging out

  if (userRole === SUPER_ADMIN) {
    const pathname = window.location.pathname;
    if (pathname.includes('/admin')) {
      if (!settings.includes('Web Portal')) settings.splice(2, 0, 'Web Portal');
    } else {
      if (!settings.includes('Dashboard')) settings.splice(2, 0, 'Dashboard');
    }
  }

  if (userRole !== COMPANY_USER) {
    settings.push('Reports');
  }

  settings.push('Logout');

  const handleOpenUserMenu = (e) => setAnchorElUser(e.currentTarget);
  const handleCloseUserMenu = () => { setAnchorElUser(null); forceCloseReports(); };

  const handleSignOut = () => {
    setIsLoggingOut(true);
    signout(() => navigate('/login', { replace: true }));
  };
  const handleChangePassword = () => dispatch(setChangePasswordOpen(!is_open));



  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {

      if (!isOverMenuItem.current && !isOverSubmenu.current) {
        setReportsAnchor(null);
      }
    }, 100);
  };

  const forceCloseReports = () => {
    clearTimeout(closeTimer.current);
    isOverMenuItem.current = false;
    isOverSubmenu.current = false;
    setReportsAnchor(null);
  };



  const onReportsEnter = (e) => {
    isOverMenuItem.current = true;
    clearTimeout(closeTimer.current);
    setReportsAnchor(e.currentTarget);
  };

  const onReportsLeave = () => {
    isOverMenuItem.current = false;
    scheduleClose();
  };



  const onSubmenuEnter = () => {
    isOverSubmenu.current = true;
    clearTimeout(closeTimer.current);
  };

  const onSubmenuLeave = () => {
    isOverSubmenu.current = false;
    scheduleClose();
  };


  const handleReportChoice = (type) => {
    setReportType(type);
    setDownloadModalOpen(true);
    handleCloseUserMenu();
  };

  if (isLoggingOut) return null; // Alternatively, return a minimal box to maintain layout spacing

  return (
    <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', pr: '20px' }}>

      <Typography
        color='textPrimary'
        variant="subtitle2"
        sx={{ mr: 1, textTransform: 'capitalize', display: { md: 'flex', xs: 'none' } }}
      >
        {auth?.first_name} {auth?.last_name}
      </Typography>

      <Tooltip title="Open settings">
        <IconButton
          onClick={handleOpenUserMenu}
          sx={{ p: 0.2, border: 1, borderColor: '#D9D9D9' }}
        >
          <Avatar
            alt={auth?.first_name}
            src="/static/images/avatar/2.jpg"
            sx={{ width: 35, height: 35, bgcolor: 'lightslategrey', textTransform: 'uppercase' }}
          />
        </IconButton>
      </Tooltip>

      {/* ─── MAIN MENU ─────────────────────────────────────────────────────── */}
      <Menu
        sx={{ mt: '45px' }}
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableAutoFocusItem
      >
        {!isLoggingOut && settings.map((setting) => (
          <MenuItem
            key={setting}
            onMouseEnter={(e) => {
              if (setting === 'Reports') {
                onReportsEnter(e);
              } else {
                // Moving to another item → close submenu
                isOverMenuItem.current = false;
                scheduleClose();
              }
            }}
            onMouseLeave={() => {
              if (setting === 'Reports') onReportsLeave();
            }}
            onClick={() => {
              if (setting === 'Reports') return;           // handled by hover
              handleCloseUserMenu();
              if (setting === 'Dashboard') window.location.replace('/admin');
              if (setting === 'Web Portal') window.location.replace('/');
              if (setting === 'Brand Library') navigate('/brand-library');
              if (setting === 'Logout') handleSignOut();
              if (setting === 'Change Password') handleChangePassword();
            }}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              bgcolor: setting === 'Reports' && reportsAnchor ? 'rgba(0,0,0,0.04)' : 'transparent',
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{setting}</Typography>
            {setting === 'Reports' && <ChevronRightIcon fontSize="small" />}
          </MenuItem>
        ))}
      </Menu>

      {/* ─── REPORTS SUBMENU (Popper + Grow for smooth animation) ─────────── */}
      <Popper
        open={Boolean(reportsAnchor)}
        anchorEl={reportsAnchor}
        placement="left-start"
        transition
        style={{ zIndex: 1400 }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },    // 8px gap from the main menu
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={160}>
            <Paper
              elevation={4}
              onMouseEnter={onSubmenuEnter}
              onMouseLeave={onSubmenuLeave}
              sx={{
                minWidth: 220,
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #f0f0f0',
              }}
            >
              <MenuList dense disablePadding>
                <MenuItem
                  onClick={() => handleReportChoice('correction')}
                  sx={{ py: 1.2, '&:hover': { bgcolor: 'rgba(255,97,0,0.06)' } }}
                >
                  <Typography variant="body2">Job Correction Report</Typography>
                </MenuItem>

                <MenuItem
                  onClick={() => handleReportChoice('completion')}
                  sx={{ py: 1.2, '&:hover': { bgcolor: 'rgba(255,97,0,0.06)' } }}
                >
                  <Typography variant="body2">Job Completion Report</Typography>
                </MenuItem>
              </MenuList>
            </Paper>
          </Grow>
        )}
      </Popper>

      <DownloadReportModal
        open={downloadModalOpen}
        handleClose={() => setDownloadModalOpen(false)}
        reportType={reportType}
      />

    </Box>
  );
};

export default ToolbarAccount;